import {
  API_BASE_URL,
  API_BASE_URL_V3,
  API_BASE_URL_V4
} from "@/src/common/config"
import { City } from "@/src/common/types"
import {
  Author,
  DatasetPageData,
  GetAlertProps,
  InitiatePaymentPayload,
  InitiatePaymentResponse,
  IntelligenceQueryParams,
  InviteMemberPayload,
  LicenceInformation,
  MemberProfile,
  MembershipPlan,
  Report,
  ReportSummary,
  SidebarMenuItemV2,
  WebflowPageData
} from "@/src/sb/business-layer/types"
import { PaginatedResponse } from "@/src/sb/core"
import { ProfileUpdate } from "@/src/sb/core/validatorSchemas/profile-update"
import qs from "qs"

import {
  SB_ARTICLE_API_URL,
  SB_ARTICLE_AUTHOR_API_URL,
  SB_ARTICLE_SUMMARIES_API_URL,
  SB_COUNTRIES_API,
  SB_DATASET_API_URL,
  SB_INITIATE_PAYMENT_API_URL,
  SB_INVITE_TEAM_MEMBER_API_URL,
  SB_LICENCE_INFORMATION_API_URL,
  SB_MEMBER_GET_ALERT_API_URL,
  SB_MEMBER_PROFILE_API_URL,
  SB_PLANS_API_URL,
  SB_RESEND_LICENSE_MEMBER_INVITE_API_URL,
  SB_SEARCH_API_URL,
  SB_SEARCH_FILTERS_API,
  SB_SEMANTIC_SEARCH_API_URL,
  SB_TAGS_CLASSIFICATION_API_URL,
  SB_TEAM_LICENSE_MEMBER_API_URL
} from "../../business-layer/config"
import ExternalHTTPInstance from "./ExternalHTTP"

export class SBAPI {
  constructor(private readonly externalHTTPInstance = ExternalHTTPInstance) {}

  public async fetchMembershipPlans(): Promise<MembershipPlan[]> {
    return this.externalHTTPInstance.get<MembershipPlan[]>(SB_PLANS_API_URL)
  }

  public async getArticleWrappedInList(
    {
      slug,
      truncated
    }: {
      slug: string
      truncated?: boolean
    },
    token?: string
  ): Promise<Report[]> {
    return this.externalHTTPInstance.get<Report[]>(SB_ARTICLE_API_URL, token, {
      params: { slug, truncated: Boolean(truncated) }
    })
  }

  public async getDataset(
    {
      slug
    }: {
      slug: string
    },
    token?: string
  ): Promise<DatasetPageData["dataset"]> {
    return this.externalHTTPInstance.get<DatasetPageData["dataset"]>(
      `${SB_DATASET_API_URL}${slug}`,
      token
    )
  }

  public async getWebflowPage({
    pageURL
  }: {
    pageURL: string
  }): Promise<WebflowPageData["wfHtml"]> {
    return this.externalHTTPInstance.get<WebflowPageData["wfHtml"]>(
      pageURL,
      undefined,
      { transformResponse: undefined }
    )
  }

  public async getAuthor(id: string): Promise<Author> {
    return this.externalHTTPInstance.get<Author>(
      `${SB_ARTICLE_AUTHOR_API_URL}${id}`
    )
  }

  public async fetchArticleSummaries(
    queryParams: Record<string, any>
  ): Promise<PaginatedResponse<ReportSummary>> {
    return this.externalHTTPInstance.get<PaginatedResponse<ReportSummary>>(
      SB_ARTICLE_SUMMARIES_API_URL,
      undefined,
      {
        params: {
          personalized: "false",
          sort: "desc",
          limit: "6",
          ...queryParams
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: "repeat" })
        }
      }
    )
  }

  public async initiateSubscriptionPayment(
    payload: InitiatePaymentPayload,
    token: string
  ): Promise<InitiatePaymentResponse> {
    return this.externalHTTPInstance.post<InitiatePaymentResponse>(
      SB_INITIATE_PAYMENT_API_URL,
      payload,
      token
    )
  }

  public async updateMemberProfile(
    payload: ProfileUpdate,
    token: string
  ): Promise<ProfileUpdate> {
    return this.externalHTTPInstance.patch<ProfileUpdate>(
      SB_MEMBER_PROFILE_API_URL,
      payload,
      token
    )
  }

  public async getIntelligence({
    q,
    tags,
    type,
    limit = 5,
    page = 1,
    sort,
    exclude_country_risk,
    use_semantic_search
  }: IntelligenceQueryParams): Promise<any> {
    let apiUrl =
      type === "reports" && use_semantic_search
        ? SB_SEMANTIC_SEARCH_API_URL
        : SB_SEARCH_API_URL

    const useSearchApiEndpoint = Boolean(q)

    let params: Omit<IntelligenceQueryParams, "tags"> & {
      "tag.slug"?: Array<string>
    } = {
      limit,
      sort
    }

    let baseUrl

    if (!useSearchApiEndpoint) {
      if (type !== "reports" && type !== "datasets") {
        throw new Error(`Invalid request`)
      }

      params = {
        ...params,
        "tag.slug": tags,
        offset: Math.max((page - 1) * limit, 0),
        ...(exclude_country_risk !== undefined && { exclude_country_risk })
      }

      apiUrl =
        type === "reports" ? SB_ARTICLE_SUMMARIES_API_URL : SB_DATASET_API_URL

      baseUrl = API_BASE_URL
    } else {
      // `page` is zero-based index for the search endpoint
      params = {
        ...params,
        q,
        "tag.slug": tags,
        page: Math.max(page - 1, 0)
      }

      if (type) {
        params.type = type
      }

      baseUrl =
        type === "reports" && use_semantic_search
          ? API_BASE_URL
          : API_BASE_URL_V3
    }

    this.externalHTTPInstance.getAxios().defaults.paramsSerializer = function (
      params
    ) {
      return qs.stringify(params, { arrayFormat: "repeat" })
    }

    return await this.externalHTTPInstance.get<any>(apiUrl, undefined, {
      baseURL: baseUrl,
      params
    })
  }

  public async getMemberProfile(token: string): Promise<MemberProfile> {
    return await this.externalHTTPInstance.get<MemberProfile>(
      SB_MEMBER_PROFILE_API_URL,
      token
    )
  }

  public async subscribeToGetAlert(
    payload: GetAlertProps[],
    token: string
  ): Promise<void> {
    return this.externalHTTPInstance.post<void>(
      SB_MEMBER_GET_ALERT_API_URL,
      payload,
      token
    )
  }

  public async fetcher(
    url: string,
    params: object
  ): Promise<Record<string, Record<string, any>>> {
    return this.externalHTTPInstance.get(url, undefined, { params })
  }

  public async fetchCities(
    url: string,
    token: string,
    params: object
  ): Promise<City[]> {
    return this.externalHTTPInstance.get(url, token, { params })
  }

  public async fetchLicenceInformation(
    token: string
  ): Promise<LicenceInformation> {
    return await this.externalHTTPInstance.get(
      SB_LICENCE_INFORMATION_API_URL,
      token
    )
  }

  public async inviteTeamMember(
    payload: InviteMemberPayload[],
    token: string
  ): Promise<any> {
    return await this.externalHTTPInstance.post(
      SB_INVITE_TEAM_MEMBER_API_URL,
      payload,
      token
    )
  }

  public async deleteTeamMember(email: string, token: string): Promise<any> {
    return await this.externalHTTPInstance.delete(
      `${SB_TEAM_LICENSE_MEMBER_API_URL}/${email}`,
      token
    )
  }

  public async resendTeamMemberInvite(
    email: string,
    token: string
  ): Promise<any> {
    return await this.externalHTTPInstance.post(
      `${SB_RESEND_LICENSE_MEMBER_INVITE_API_URL}`,
      { email },
      token
    )
  }

  public async fetchGlobalSearchFilters(): Promise<any> {
    return await this.externalHTTPInstance.get(`${SB_SEARCH_FILTERS_API}`)
  }

  public async fetchGlobalSearchResults(
    params: {
      q?: string
      filter_id?: number | string
      "tag.slug"?: string[]
      page?: number
      limit?: number
      "use-semantic-reports"?: boolean
    },
    signal?: AbortSignal
  ): Promise<any> {
    return await this.externalHTTPInstance.get(
      `${SB_SEARCH_API_URL}`,
      undefined,
      {
        baseURL: API_BASE_URL_V4,
        params,
        signal
      }
    )
  }

  public async fetchV2NavigationMenu(config: Record<string, any> = {}) {
    return await this.externalHTTPInstance.get<
      Record<string, SidebarMenuItemV2[]>
    >(SB_TAGS_CLASSIFICATION_API_URL, undefined, config)
  }

  public async fetchCountries(params: Record<string, any>): Promise<any> {
    return await this.externalHTTPInstance.get(
      `${SB_COUNTRIES_API}`,
      undefined,
      {
        baseURL: API_BASE_URL,
        params
      }
    )
  }
}

const SBAPIInstance = new SBAPI(ExternalHTTPInstance)
export default SBAPIInstance
